import { useEffect, useMemo, useState } from 'react';
import { ActivityIndicator, KeyboardAvoidingView, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useTheme } from '@/lib/styling';
import type { Theme } from '@/theme';
import { Box, PressableBox, Text } from '@/components/ui/primitives';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { betterAuthClient } from '@/lib/betterAuthClient';
import { clientLogger } from '@/lib/datadog';
import { getAuthHref, getForgotPasswordHref, normalizeStringParam } from '@/lib/routes';
import { isIOS } from '@/lib/platform';

const PasswordInput = ({
  value,
  onChangeText,
  placeholder,
  editable,
  testID,
}: {
  value: string;
  onChangeText: (value: string) => void;
  placeholder: string;
  editable: boolean;
  testID: string;
}) => {
  const theme = useTheme<Theme>();

  return (
    <Input
      value={value}
      onChangeText={onChangeText}
      placeholder={placeholder}
      secureTextEntry
      autoComplete="new-password"
      editable={editable}
      testID={testID}
      style={[{ paddingRight: theme.spacing.m }]}
    />
  );
};

export default function ResetPasswordScreen() {
  const router = useRouter();
  const params = useLocalSearchParams<{ token?: string | string[] }>();
  const theme = useTheme<Theme>();
  const token = useMemo(() => normalizeStringParam(params.token), [params.token]);
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(
    token ? null : 'This reset link is missing its token.',
  );
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  useEffect(() => {
    if (!successMessage) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      router.replace(getAuthHref());
    }, 1500);

    return () => clearTimeout(timeout);
  }, [router, successMessage]);

  const handleSubmit = async () => {
    if (!token) {
      setErrorMessage('This reset link is invalid or expired.');
      return;
    }
    if (password.length < 8) {
      setErrorMessage('Your password must be at least 8 characters.');
      return;
    }
    if (password !== confirmation) {
      setErrorMessage('Passwords do not match.');
      return;
    }

    setIsSubmitting(true);
    setErrorMessage(null);
    setSuccessMessage(null);

    try {
      const result = await betterAuthClient.resetPassword({
        token,
        newPassword: password,
      });

      if (result.error) {
        throw new Error(result.error.message || 'Password reset failed');
      }

      setSuccessMessage('Password updated. You can now sign in with your new password.');
    } catch (error) {
      clientLogger.error('Password reset failed', { error });
      setErrorMessage('This reset link is invalid or expired. Request a new link to try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasSucceeded = Boolean(successMessage);

  return (
    <Box style={styles.screen}>
      <Box style={styles.gridLayer} />
      <ScrollView contentContainerStyle={styles.content} keyboardShouldPersistTaps="handled">
        <KeyboardAvoidingView behavior={isIOS ? 'padding' : undefined} style={styles.panel}>
          <Box style={styles.header}>
            <Text variant="caption" style={styles.kicker}>ACCOUNT ACCESS / RECOVERY</Text>
            <Box style={styles.titleWrap}>
              <Box style={styles.acidSlab} />
              <Text variant="h2" style={styles.title}>Set a new password</Text>
            </Box>
            <Text variant="body" color="pressMuted">
              Choose a new password for your PCKM account.
            </Text>
          </Box>

          {!hasSucceeded ? (
            <Box gap="m">
              <PasswordInput
                value={password}
                onChangeText={setPassword}
                placeholder="New password"
                editable={!isSubmitting && Boolean(token)}
                testID="reset-password-input"
              />
              <PasswordInput
                value={confirmation}
                onChangeText={setConfirmation}
                placeholder="Confirm new password"
                editable={!isSubmitting && Boolean(token)}
                testID="reset-password-confirm-input"
              />
            </Box>
          ) : null}

          {errorMessage ? <Text color="destructive">{errorMessage}</Text> : null}
          {successMessage ? <Text color="success">{successMessage}</Text> : null}

          {!hasSucceeded && token ? (
            <Button
              onPress={() => void handleSubmit()}
              disabled={isSubmitting}
              size="lg"
              testID="reset-password-submit-button"
            >
              {isSubmitting ? (
                <ActivityIndicator color={theme.colors.press} />
              ) : (
                <Text variant="button" color="primaryForeground">Reset password</Text>
              )}
            </Button>
          ) : null}

          {hasSucceeded ? (
            <Button onPress={() => router.replace(getAuthHref())} size="lg">
              <Text variant="button" color="primaryForeground">Return to sign in</Text>
            </Button>
          ) : (
            <PressableBox onPress={() => router.replace(getForgotPasswordHref())} alignSelf="center" padding="s">
              <Text variant="button" color="press">Request a new link</Text>
            </PressableBox>
          )}
        </KeyboardAvoidingView>
      </ScrollView>
    </Box>
  );
}

const styles = StyleSheet.create((theme, runtime) => {
  const isWide = runtime.screen.width >= theme.breakpoints.sm;

  return {
    acidSlab: {
      backgroundColor: theme.colors.acid,
      bottom: -4,
      height: 6,
      left: 0,
      position: 'absolute',
      right: 8,
    },
    content: {
      alignItems: 'center',
      flexGrow: 1,
      justifyContent: 'center',
      paddingBottom: 48,
      paddingHorizontal: isWide ? 32 : 16,
      paddingTop: isWide ? 76 : 36,
    },
    gridLayer: {
      borderColor: theme.colors.press,
      borderWidth: 1,
      bottom: 0,
      left: 0,
      opacity: 0.1,
      position: 'absolute',
      right: 0,
      top: 0,
      transform: [{ rotate: '1deg' }],
    },
    header: { gap: 12 },
    kicker: { color: theme.colors.pressMuted },
    panel: {
      backgroundColor: theme.colors.background,
      borderColor: theme.colors.press,
      borderWidth: 5,
      elevation: 8,
      gap: theme.spacing.l,
      maxWidth: 560,
      padding: isWide ? 28 : 18,
      shadowColor: theme.colors.press,
      shadowOffset: { width: 14, height: 14 },
      shadowOpacity: 1,
      shadowRadius: 0,
      width: '100%',
    },
    screen: { backgroundColor: theme.colors.background, flex: 1 },
    title: { color: theme.colors.press },
    titleWrap: { alignSelf: 'flex-start', position: 'relative' },
  };
});
